import { useEffect } from "react";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $getRoot, $createTextNode, $isTextNode } from "lexical";
import { $generateNodesFromDOM } from "@lexical/html";
import { $createVariableNode } from "./VariableNode";
import { CONTRACT_VARIABLES, SIGNATURE_VARIABLE_KEY } from "../variables";

// Build a lookup of known variable metadata so we can present the correct
// display label + signature styling for each `{Var_Name}` we substitute.
const VARIABLE_LOOKUP = (() => {
  const map = {};
  for (const group of CONTRACT_VARIABLES) {
    for (const item of group.items) {
      map[item.key] = {
        label: item.label,
        isSignature: !!item.isSignature || item.key === SIGNATURE_VARIABLE_KEY,
      };
    }
  }
  return map;
})();

const PLACEHOLDER_REGEX = /\{([A-Za-z0-9_]+)\}/g;

// Walk `node`'s subtree, splitting each text node on `{Var_Name}` patterns
// and replacing matched fragments with VariableNode chips. Preserves the
// original text node's format bits (bold/italic/etc.) on the surrounding
// text spans so formatting doesn't get dropped around a chip.
const replacePlaceholdersInTree = (node) => {
  const children = node.getChildren ? node.getChildren() : [];
  for (const child of children) {
    if ($isTextNode(child)) {
      const text = child.getTextContent();
      PLACEHOLDER_REGEX.lastIndex = 0;
      if (!PLACEHOLDER_REGEX.test(text)) continue;

      PLACEHOLDER_REGEX.lastIndex = 0;
      const format = child.getFormat();
      const parts = [];
      let lastIndex = 0;
      let match;
      while ((match = PLACEHOLDER_REGEX.exec(text)) !== null) {
        if (match.index > lastIndex) {
          const before = $createTextNode(text.slice(lastIndex, match.index));
          before.setFormat(format);
          parts.push(before);
        }
        const varName = match[1];
        const meta = VARIABLE_LOOKUP[varName];
        if (meta) {
          parts.push(
            $createVariableNode(varName, meta.label, meta.isSignature)
          );
        } else {
          // Unknown placeholder — keep the raw text so the user sees it and
          // can fix it, rather than silently swallowing.
          const raw = $createTextNode(match[0]);
          raw.setFormat(format);
          parts.push(raw);
        }
        lastIndex = match.index + match[0].length;
      }
      if (lastIndex < text.length) {
        const after = $createTextNode(text.slice(lastIndex));
        after.setFormat(format);
        parts.push(after);
      }
      for (const part of parts) child.insertBefore(part);
      child.remove();
    } else if (child.getChildren) {
      replacePlaceholdersInTree(child);
    }
  }
};

// One-shot plugin: parses the provided HTML and seeds the editor with it.
// ImageNode.importDOM() converts `<img>` into ImageNode during parse;
// `{Var_Name}` text placeholders become VariableNodes via a subsequent walk.
const ContentInitPlugin = ({ initialContent }) => {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    if (initialContent == null) return;
    editor.update(() => {
      const root = $getRoot();
      root.clear();
      const parser = new DOMParser();
      const dom = parser.parseFromString(initialContent, "text/html");
      const nodes = $generateNodesFromDOM(editor, dom);
      if (nodes.length) {
        root.append(...nodes);
      }
      replacePlaceholdersInTree(root);
    });
    // Intentionally run only on initial mount; further changes are user edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
};

export default ContentInitPlugin;
